import { EventEmitter, Component, OnInit, Output, Input, ElementRef, Renderer2 } from "@angular/core";
import { FullpageService } from "../fullpage.service";

@Component({
    selector: "bluesky",
    templateUrl: "./bluesky.component.html",
    styleUrls: ["./bluesky.component.scss"]
})

export class BlueskyComponent implements OnInit {
    @Output() hover = new EventEmitter<boolean>();
    @Input("show") showOnInit: boolean = false;
    apiUrl: string = '';
    twitterScreenName: string = '';
    tweets: any;
    fullpage_api: any;
    isOnScroll: boolean = false;

    constructor(private el: ElementRef, private renderer: Renderer2, private fullpageService: FullpageService) { }

    ngOnInit(): void {
        // Setup visibility logic immediately
        this.setupVisibilityLogic();

        // Setup wheel event listener for immediate scrolling support
        this.setupWheelEventListener();
    }

    private setupVisibilityLogic(): void {
        // Simple approach: use intersection observer to detect which section is active
        this.observeSections();

        // Fallback: periodically check which section is active
        setInterval(() => {
            this.checkCurrentSection();
        }, 500);
    }

    private observeSections(): void {
        const sections = document.querySelectorAll('.section');
        if (sections.length === 0) {
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
                    const sectionElement = entry.target as HTMLElement;
                    const isHomeSection = sectionElement.classList.contains('section_home');

                    // Don't show on first section for both mobile and desktop
                    this.toggleIconVisibility(!isHomeSection);
                }
            });
        }, {
            threshold: 0.5
        });

        sections.forEach(section => observer.observe(section));
    }

    private checkCurrentSection(): void {
        // Fallback method: check which section is in viewport
        const sections = document.querySelectorAll('.section');
        const viewportHeight = window.innerHeight;

        for (let i = 0; i < sections.length; i++) {
            const section = sections[i] as HTMLElement;
            const rect = section.getBoundingClientRect();

            // Check if this section is mostly in viewport
            if (rect.top <= viewportHeight * 0.3 && rect.bottom >= viewportHeight * 0.7) {
                const isHomeSection = section.classList.contains('section_home');

                // Don't show on first section for both mobile and desktop
                this.toggleIconVisibility(!isHomeSection);
                break;
            }
        }
    }

    private toggleIconVisibility(show: boolean): void {
        const icon = this.el.nativeElement.querySelector('.twitterTogglerImg');

        if (icon) {
            if (show) {
                this.renderer.setStyle(icon, 'opacity', '1');
                this.renderer.setStyle(icon, 'pointer-events', 'auto');
                this.renderer.setStyle(icon, 'display', 'inline-block');
            } else {
                this.renderer.setStyle(icon, 'opacity', '0');
                this.renderer.setStyle(icon, 'pointer-events', 'none');
            }
        }
    }

    private setupWheelEventListener(): void {
        // Add wheel event listener to the document to catch all wheel events
        document.addEventListener('wheel', this.handleWheelEvent.bind(this), { passive: false });
    }

    toggleTwitter(collapse?: boolean): void {
        const twitterDisplay = document.querySelector('.twitterdisplay');
        if (collapse !== false) {
            if (twitterDisplay?.classList.contains('collapsed')) {
                this.setTwitter(true);
            } else if (twitterDisplay && !twitterDisplay.classList.contains('collapsed')) {
                this.setTwitter(false);
            }
        } else {
            this.setTwitter(collapse);
        }
    }

    setTwitter(show: boolean): void {
        const twitterDisplay = document.querySelector('.twitterdisplay');

        if (twitterDisplay) {
            if (show) {
                twitterDisplay.classList.remove('collapsed');
                // Let CSS handle responsive widths - remove inline styles
                (twitterDisplay as HTMLElement).style.width = '';
                (twitterDisplay as HTMLElement).style.maxWidth = '';

                // Trigger load more content after a delay
                setTimeout(() => {
                    this.triggerLoadMore();
                    this.ensureScrollableContent();
                }, 100);

                // Try again after a longer delay to ensure content is loaded
                setTimeout(() => {
                    this.triggerLoadMore();
                    this.ensureScrollableContent();
                }, 2000);
            } else {
                twitterDisplay.classList.add('collapsed');
                // Collapse the twitter panel
                (twitterDisplay as HTMLElement).style.width = '0px';
                (twitterDisplay as HTMLElement).style.maxWidth = 'none';
            }
        }
    }

    toggleHoverStatus(over: boolean) {
        this.isOnScroll = over;
        this.hover.emit(over);
    }

    openBlueskyProfile(): void {
        // Open the VeeningLab bluesky profile in a new window
        window.open('https://bsky.app/profile/veeninglab.com', '_blank');
    }

    handleIconClick(): void {
        const isMobile = window.innerWidth <= 768;

        if (isMobile) {
            // On mobile: open bluesky profile in new window
            this.openBlueskyProfile();
        } else {
            // On laptop/desktop: toggle the bluesky component
            this.toggleTwitter();
        }
    }

    private handleWheelEvent(event: WheelEvent): void {
        // Check if the event target is within the bluesky feed
        const blueskyContainer = this.el.nativeElement.querySelector('.twitterdisplay');

        if (blueskyContainer && !blueskyContainer.classList.contains('collapsed') && blueskyContainer.contains(event.target as Node)) {
            // Only prevent fullpage scrolling when bluesky is open and we're scrolling within it
            event.preventDefault();
            event.stopPropagation();
            // Get scroll amount from the event (keep original direction for trackpad)
            let scrollAmount = event.deltaY;

            // If DeltaY is 0, try DeltaX
            if (scrollAmount === 0 && event.deltaX !== 0) {
                scrollAmount = event.deltaX;
            }

            // If still 0, use a default amount
            if (scrollAmount === 0) {
                scrollAmount = event.deltaMode === 1 ? 40 : 20;
            }

            // Try to scroll the bluesky embed content first
            const bskyEmbed = blueskyContainer.querySelector('bsky-embed');
            if (bskyEmbed) {
                const embedContent = bskyEmbed.querySelector('.bsky-embed') || bskyEmbed;
                if (embedContent && embedContent.scrollHeight > embedContent.clientHeight) {
                    embedContent.scrollTop += scrollAmount;
                    return;
                }
            }

            // Fallback to scrolling the container
            blueskyContainer.scrollTop += scrollAmount;
        }
    }



    private triggerLoadMore(): void {
        const blueskyContainer = this.el.nativeElement.querySelector('.twitterdisplay');
        if (blueskyContainer) {
            const bskyEmbed = blueskyContainer.querySelector('bsky-embed');
            if (bskyEmbed) {
                // Try multiple selectors for load more button
                const loadMoreSelectors = [
                    '[data-load-more]',
                    '.load-more',
                    'button',
                    '[role="button"]',
                    '.bsky-load-more',
                    '.load-more-button',
                    'a[href="#"]',
                    '.more-posts',
                    '.load-more-posts'
                ];

                let loadMoreButton = null;
                for (const selector of loadMoreSelectors) {
                    loadMoreButton = bskyEmbed.querySelector(selector);
                    if (loadMoreButton) {
                        break;
                    }
                }

                if (loadMoreButton) {
                    (loadMoreButton as HTMLElement).click();

                    // Try clicking again after a delay
                    setTimeout(() => {
                        (loadMoreButton as HTMLElement).click();
                    }, 500);
                } else {
                    this.tryAlternativeLoadMore();
                }
            }
        }
    }

    private tryAlternativeLoadMore(): void {
        const blueskyContainer = this.el.nativeElement.querySelector('.twitterdisplay');
        if (blueskyContainer) {
            const bskyEmbed = blueskyContainer.querySelector('bsky-embed');
            if (bskyEmbed) {
                // Try to scroll to bottom to trigger lazy loading
                const embedContent = bskyEmbed.querySelector('.bsky-embed') || bskyEmbed;
                if (embedContent) {
                    embedContent.scrollTop = embedContent.scrollHeight;

                    // Try scrolling again after a delay
                    setTimeout(() => {
                        embedContent.scrollTop = embedContent.scrollHeight;
                    }, 1000);
                }

                // Try dispatching a custom event
                const loadMoreEvent = new CustomEvent('load-more', { bubbles: true });
                bskyEmbed.dispatchEvent(loadMoreEvent);
            }
        }
    }

    private ensureScrollableContent(): void {
        const blueskyContainer = this.el.nativeElement.querySelector('.twitterdisplay');
        if (blueskyContainer) {
            const bskyEmbed = blueskyContainer.querySelector('bsky-embed');
            if (bskyEmbed) {
                const embedContent = bskyEmbed.querySelector('.bsky-embed') || bskyEmbed;
                if (embedContent) {
                    // Check if content is scrollable
                    const isScrollable = embedContent.scrollHeight > embedContent.clientHeight;

                    if (!isScrollable) {
                        // Try to scroll to bottom to trigger lazy loading
                        embedContent.scrollTop = embedContent.scrollHeight;

                        // Wait a bit and check again
                        setTimeout(() => {
                            const newIsScrollable = embedContent.scrollHeight > embedContent.clientHeight;

                            if (!newIsScrollable) {
                                // Add some dummy content to make it scrollable
                                const dummyContent = document.createElement('div');
                                dummyContent.style.height = '1000px';
                                dummyContent.style.backgroundColor = 'transparent';
                                dummyContent.style.pointerEvents = 'none';
                                dummyContent.innerHTML = '<div style="height: 100%;"></div>';

                                embedContent.appendChild(dummyContent);
                            }
                        }, 500);
                    }
                }
            }
        }
    }
}